/**
 * Criterion Mapping (LEGACY / v1) — Phase 3 of the 4-phase OBS appointment.
 *
 * This is the original task-by-task mapping experience, preserved verbatim for
 * every autism patient whose stamped workflow config is v1 (i.e. not
 * obsFlow.version === 'v2'). The v2 redesign lives in 3_CriterionMappingV2.js;
 * 3_CriterionMapping.js chooses between the two on the resolved workflow config.
 * Do not add v2-only behaviour here — this path must stay byte-for-byte
 * equivalent to the pre-redesign flow so in-flight v1 assessments never change.
 *
 * For each selected observation task the clinician maps master notes onto
 * DSM-5 criteria and records evidence decisions. Primary footer CTAs step
 * task ↔ task; grey ModularPhaseSkipLink footers jump back to ADOS-informed
 * coding or forward to Review and Synthesis.
 *
 * Review is always clickable (SSI pattern): when any selected task still lacks
 * evidence decisions we show a warning dialog ("Proceed Anyway" / go back to
 * the first incomplete task).
 *
 * LLM generation (SSI parity):
 *   • Criteria paragraphs are generated when the clinician LEAVES this phase
 *     through any managed exit (back to ADOS, forward to Review) or through an
 *     unmanaged one (breadcrumb / browser back, via the exit-regeneration hook).
 *     Switching between tasks inside the phase does NOT generate — we want one
 *     run against the final set of inputs, not one per click.
 *   • Partial mapping still generates, provided at least one evidence decision
 *     exists somewhere; only a completely undecided phase is skipped.
 *   • Edits (notes or decisions) re-dirty the section so the next exit
 *     regenerates.
 *   • Notes can also be moved in/out of Self-Report from this phase, which
 *     dirties that separate section and regenerates it on exit.
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { ExclamationIcon } from '@heroicons/react/solid';
import Layout from '../../../../components/Layout';
import Breadcrumb from '../../../../components/Breadcrumb';
import PreviewModeBanner from '../../../../components/PreviewModeBanner';
import ObservationTaskSelector from '../components/ObservationTaskSelector';
import ObservationTaskPanelV2a from '../components/ObservationTaskPanelV2a';
import ModularStepFooter from '../../../../components/autismModular/ModularStepFooter';
import { useDragAutoScroll } from '../../../../hooks/useDragAutoScroll';
import { useModularSectionExitRegeneration } from '../../../../hooks/useModularSectionExitRegeneration';
import { TriggerGenerateDocumentSection } from '../../../../utils/TriggerGenerateDocumentSection';
import { getAutismOBSStepRoutes } from '../../../../utils/autismObsRouting';
import {
  hasAnyObservationCriterionDecision,
  listIncompleteObservationCriteriaTasks,
} from '../../../../utils/observationCriteriaCompletion';
import {
  shouldGenerateSelfReportSection,
  hasSelfReportNoteContent,
  noteMoveTouchesSelfReport,
} from '../../../../utils/obsSelfReportGeneration';
import useObservationStageData from '../hooks/useObservationStageData';

export default function CriterionMappingLegacy() {
  const {
    patientId,
    tasks,
    navigate,
    flushStageWrites,
    isPreviewMode,
    canEdit,
    reportContent,
    selectedTaskIds,
    allTaskData,
    adosData,
    handleFieldChange,
    noteMoveTargets,
    handleMoveNoteAcross,
  } = useObservationStageData();

  // Track edits made in this phase so an exit regenerates the paragraphs.
  const [hasChanges, setHasChanges] = useState(false);
  const [showValidationDialog, setShowValidationDialog] = useState(false);

  // Generation gate: at least one evidence decision somewhere. Partial mapping
  // is fine — a phase with no decisions at all is not.
  const canGenerateCriteria = useMemo(
    () => hasAnyObservationCriterionDecision(selectedTaskIds, tasks, allTaskData),
    [selectedTaskIds, tasks, allTaskData],
  );

  // Tasks still missing one or more evidence decisions — feeds the warning list.
  const incompleteTasks = useMemo(
    () => listIncompleteObservationCriteriaTasks(selectedTaskIds, tasks, allTaskData),
    [selectedTaskIds, tasks, allTaskData],
  );

  const { markRegenHandled } = useModularSectionExitRegeneration({
    patientId,
    stageKey: 'observation',
    sectionTitle: 'Criteria',
    hasChanges,
    canEdit,
    reportContent,
    shouldRegenerate: canGenerateCriteria,
  });

  const flushAndGenerateCriteria = useCallback(async () => {
    markRegenHandled();
    const dataReady = flushStageWrites('observation');
    if (canEdit && canGenerateCriteria) {
      const stageReport = reportContent?.observation;
      const generated = stageReport?.stageSectionsGenerated || [];
      const isGenerated = generated.includes('Criteria');
      if (hasChanges || !isGenerated) {
        TriggerGenerateDocumentSection(
          patientId, 'Autism Behavioural Observation', 'Criteria', [], dataReady,
        );
      }
    }
    return dataReady;
  }, [markRegenHandled, flushStageWrites, canEdit, canGenerateCriteria, reportContent, hasChanges, patientId]);

  // ── Self-Report: notes can be moved in/out of that box from this phase ──
  // Task Administration owns the Self-Report panel, but its notes are a valid
  // move destination everywhere, so a move made here must also refresh that
  // paragraph. Tracked separately from Criteria because it is its own report
  // section with its own "don't generate an empty paragraph" rule.
  const [selfReportDirty, setSelfReportDirty] = useState(false);
  const selfReportHandledRef = useRef(false);

  const selfReportGenerationArgs = useCallback((allowInitialGeneration = false) => ({
    hasContent: hasSelfReportNoteContent(allTaskData, adosData),
    hasChanges: selfReportDirty,
    isGenerated: Boolean(
      reportContent?.observation?.stageSectionsGenerated?.includes('Self-Report'),
    ),
    // First Self-Report draft is owned by the forward step into Review
    // (alongside Criteria), matching the v2 Criterion Mapping rule.
    allowInitialGeneration,
  }), [allTaskData, adosData, selfReportDirty, reportContent]);

  const maybeGenerateSelfReport = useCallback((dataReady, allowInitialGeneration = false) => {
    if (!canEdit) return;
    if (!shouldGenerateSelfReportSection(selfReportGenerationArgs(allowInitialGeneration))) return;
    selfReportHandledRef.current = true;
    TriggerGenerateDocumentSection(
      patientId, 'Autism Behavioural Observation', 'Self-Report', [], dataReady,
    );
  }, [canEdit, selfReportGenerationArgs, patientId]);

  // Unmanaged-exit net for Self-Report (breadcrumb, browser back). Kept as a
  // plain effect rather than a second useModularSectionExitRegeneration because
  // that hook registers global navigation-guard state, and two registrations on
  // one page would clear each other. Criteria owns the guard on this page.
  // Never draft Self-Report for the first time here — regenerate only.
  const selfReportExitRef = useRef({ maybeGenerateSelfReport });
  selfReportExitRef.current = { maybeGenerateSelfReport };
  useEffect(() => () => {
    if (selfReportHandledRef.current) return;
    selfReportExitRef.current.maybeGenerateSelfReport(undefined, false);
  }, []);

  const handleMoveNoteAcrossWrapped = useCallback((sourceId, noteId, targetId, insertAt, notePatch) => {
    if (noteMoveTouchesSelfReport(sourceId, targetId)) setSelfReportDirty(true);
    handleMoveNoteAcross(sourceId, noteId, targetId, insertAt, notePatch);
  }, [handleMoveNoteAcross]);

  /**
   * Managed exit: flush pending writes once, then generate whichever report
   * sections are due. Both triggers share the same flush promise so the backend
   * only starts reading once every pending write has landed.
   *
   * @param {boolean} isForwardToReview - true when stepping into Review
   *   (initial Self-Report draft allowed); false for Back to ADOS / side exits.
   */
  const flushAndGenerateOnExit = useCallback(async (isForwardToReview = false) => {
    const dataReady = flushAndGenerateCriteria();
    maybeGenerateSelfReport(dataReady, isForwardToReview);
    await dataReady;
  }, [flushAndGenerateCriteria, maybeGenerateSelfReport]);

  const [activeTaskId, setActiveTaskId] = useState(
    () => selectedTaskIds.length > 0 ? selectedTaskIds[0] : null,
  );

  const contentRef = useRef(null);
  useDragAutoScroll(contentRef);

  const handleSelectTask = useCallback((taskId) => {
    setActiveTaskId(taskId);
  }, []);

  const handleTaskFieldChange = useCallback((fieldId, value) => {
    handleFieldChange(activeTaskId, fieldId, value);
    if (typeof fieldId === 'string' && fieldId.startsWith('dsm_')) {
      setHasChanges(true);
    }
  }, [handleFieldChange, activeTaskId]);

  const activeTask = tasks.find(t => t.id === activeTaskId);
  const activeTaskData = allTaskData[activeTaskId] || {};

  const currentIndex = useMemo(
    () => (activeTaskId != null ? selectedTaskIds.indexOf(activeTaskId) : -1),
    [selectedTaskIds, activeTaskId],
  );
  const prevTaskId = currentIndex > 0 ? selectedTaskIds[currentIndex - 1] : null;
  const nextTaskId = currentIndex >= 0 && currentIndex < selectedTaskIds.length - 1
    ? selectedTaskIds[currentIndex + 1]
    : null;
  const prevTask = prevTaskId ? tasks.find(t => t.id === prevTaskId) : null;
  const nextTask = nextTaskId ? tasks.find(t => t.id === nextTaskId) : null;

  const activeLabel = activeTask
    ? `Task ${currentIndex + 1}. ${activeTask.shortTitle}`
    : null;

  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}/adult-autism`],
    ['Adult Autism Assessment'],
    ['Behavioural Observation'],
    ['Criterion Mapping'],
    ...(activeLabel ? [[activeLabel]] : []),
  ];

  const handleBackToAdos = useCallback(async () => {
    // Backward exit — regenerate only; never a first Self-Report draft.
    await flushAndGenerateOnExit(false);
    navigate(getAutismOBSStepRoutes(patientId).adosCoding);
  }, [flushAndGenerateOnExit, navigate, patientId]);

  /** Flush + generate the due sections, then navigate to Review. */
  const navigateToReview = useCallback(async () => {
    // Forward into Review — owns the Self-Report first draft with Criteria.
    await flushAndGenerateOnExit(true);
    navigate(getAutismOBSStepRoutes(patientId).review);
  }, [flushAndGenerateOnExit, navigate, patientId]);

  /**
   * Soft gate to Review — mirrors Adult SSI:
   *   • Preview / fully mapped → go straight through
   *   • Incomplete mapping → warning dialog (Proceed Anyway still allowed)
   */
  const handleProceedToReview = useCallback(() => {
    if (!isPreviewMode && incompleteTasks.length > 0) {
      setShowValidationDialog(true);
      return;
    }
    navigateToReview();
  }, [isPreviewMode, incompleteTasks, navigateToReview]);

  const proceedToReviewAnyway = useCallback(() => {
    setShowValidationDialog(false);
    navigateToReview();
  }, [navigateToReview]);

  return (
    <Layout activePage="dashboard">
      <div className="flex items-center">
        <Breadcrumb tupleList={breadcrumbTuples} />
      </div>

      <div className="flex h-[calc(100vh-8rem)]">
        {/* Task list only — ADOS is its own phase, so no ADOS row here. */}
        <ObservationTaskSelector
          tasks={tasks}
          selectedTaskIds={selectedTaskIds}
          activeTaskId={activeTaskId}
          activeView="task"
          assessmentMode="preset"
          allTaskData={allTaskData}
          onSelectTask={handleSelectTask}
          onRemoveTask={null}
          onOpenPicker={null}
          onSelectGeneral={null}
          showReview={false}
          hideSensoryPanel
          sidebarLayout="synthesis"
          criterionMappingMode
          onToggleCriterionMapping={null}
          adosMode={false}
          onToggleAdosMode={null}
          showAdosSidebar={false}
        />

        <div className="flex-1 min-w-0 flex flex-col">
          {isPreviewMode && <PreviewModeBanner />}
          <div ref={contentRef} className="flex-1 min-h-0 overflow-y-auto">
            {activeTask ? (
              <ObservationTaskPanelV2a
                task={activeTask}
                taskData={activeTaskData}
                onFieldChange={handleTaskFieldChange}
                selectedTaskIds={selectedTaskIds}
                showObservations={false}
                criterionMappingMode={true}
                moveTargets={noteMoveTargets}
                onMoveNote={handleMoveNoteAcrossWrapped}
                readOnly={!canEdit}
                // Read-only here: the "Not administered" decision is owned by
                // Task Administration. A flagged task still greys out its table.
                onToggleNotAdministered={null}
              />
            ) : (
              <div className="flex items-center justify-center h-full px-8 text-center">
                <p className="text-sm text-gray-500">
                  No observation tasks selected. Go back to Task Administration to add tasks.
                </p>
              </div>
            )}
          </div>

          {activeTask && (
            <ModularStepFooter
              showBack={!!prevTask}
              backLabel={prevTask?.shortTitle}
              onBack={prevTask ? () => handleSelectTask(prevTaskId) : undefined}
              // First task: phase jump back is the sole left CTA (full button).
              onModuleExit={!prevTask ? handleBackToAdos : undefined}
              moduleExitLabel={!prevTask ? 'Back to ADOS-Informed Coding' : undefined}
              phaseBackLink={
                prevTask
                  ? { label: 'Back to ADOS-Informed Coding', onClick: handleBackToAdos }
                  : undefined
              }
              counterText={`Task ${currentIndex + 1} of ${selectedTaskIds.length}`}
              nextLabel={nextTask ? nextTask.shortTitle : 'Go to Review and Synthesis'}
              onNext={nextTask
                ? () => handleSelectTask(nextTaskId)
                : handleProceedToReview}
              // Mid-phase: grey skip to Review; last task promotes it to primary CTA.
              // Always enabled — incomplete mapping opens the warning dialog.
              phaseForwardLink={
                nextTask
                  ? {
                      label: 'Go to Review and Synthesis',
                      onClick: handleProceedToReview,
                    }
                  : undefined
              }
            />
          )}

          {!activeTask && (
            <ModularStepFooter
              showBack
              backLabel="ADOS-Informed Coding"
              onBack={handleBackToAdos}
              showNext={false}
            />
          )}
        </div>
      </div>

      {/* Soft gate — same pattern as Adult SSI Review validation dialog. */}
      {showValidationDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowValidationDialog(false)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl p-6 w-[560px] max-w-[90vw] text-left">
            <button
              type="button"
              onClick={() => setShowValidationDialog(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-xl leading-none"
              aria-label="Close"
            >
              &times;
            </button>
            <div className="flex items-center gap-2 mb-4">
              <ExclamationIcon className="w-6 h-6 text-amber-500 flex-shrink-0" />
              <h2 className="text-lg font-bold text-gray-900">
                Evidence Decisions Required
              </h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              The following observation tasks are missing evidence decisions for
              one or more DSM criteria.
            </p>
            <ul className="bg-red-50 rounded-lg p-3 pl-5 mb-4 list-disc list-inside space-y-1">
              {incompleteTasks.map((t) => (
                <li key={t.taskId} className="text-sm text-red-800 font-medium">
                  Task {t.displayNumber}. {t.shortTitle}
                  {t.missingCriteria.length > 0
                    ? ` (${t.missingCriteria.join(', ')})`
                    : ''}
                </li>
              ))}
            </ul>
            <p className="text-sm italic text-amber-700 bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-5">
              <strong>Note:</strong> If you proceed without completing these, the
              criteria summary may be incomplete.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  const firstMissing = incompleteTasks[0];
                  if (firstMissing) {
                    setActiveTaskId(firstMissing.taskId);
                    setShowValidationDialog(false);
                  }
                }}
                className="flex items-center py-2 px-4 rounded-md shadow-md bg-primary text-white hover:bg-primary/90 transition-all duration-200 font-semibold text-sm"
              >
                Go back to DSM Criteria
              </button>
              <button
                type="button"
                onClick={proceedToReviewAnyway}
                className="flex items-center py-2 px-4 rounded-md shadow-md text-gray-900 border border-gray-300 hover:bg-gray-50 transition-all duration-200 font-semibold text-sm"
              >
                Proceed Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
